import { useEffect, useMemo, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Search, Undo2, Trash2, MapPin, Loader2, Ruler } from "lucide-react";
import { api } from "@/lib/api";

const SQFT_PER_M2 = 10.7639104;
const SQFT_PER_ACRE = 43560;
const toRad = (d) => (d * Math.PI) / 180;

// Geodesic polygon area (spherical excess) → square meters.
function geodesicAreaM2(points) {
  if (points.length < 3) return 0;
  const R = 6378137; // earth radius (m)
  let area = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % n];
    area += toRad(p2.lng - p1.lng) * (2 + Math.sin(toRad(p1.lat)) + Math.sin(toRad(p2.lat)));
  }
  return Math.abs((area * R * R) / 2);
}

const fmt = (v, d = 0) => v.toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });

// Styled draggable vertex icon (keeps the lime aesthetic, avoids default-icon path issues).
const vertexIcon = L.divIcon({
  className: "lawn-vertex",
  html: '<span class="lawn-vertex-dot"></span>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

export default function LawnMeasureTool() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const shapeRef = useRef(null);
  const markersRef = useRef([]);

  const [points, setPoints] = useState([]);
  const [metric, setMetric] = useState(false);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [geoError, setGeoError] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const searchBoxRef = useRef(null);
  const debounceRef = useRef(null);
  const skipFetchRef = useRef(false);

  // Init map once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [39.8283, -98.5795],
      zoom: 4,
      zoomControl: true,
      maxZoom: 20,
    });
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        maxZoom: 20,
        maxNativeZoom: 19,
        attribution: "Imagery © Esri, Maxar, Earthstar Geographics",
      }
    ).addTo(map);

    layerRef.current = L.layerGroup().addTo(map);
    map.on("click", (e) => {
      setPoints((prev) => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }]);
    });

    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 250);

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Redraw markers + polygon when points change
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    markersRef.current = [];
    shapeRef.current = null;

    const latlngs = points.map((p) => [p.lat, p.lng]);

    if (points.length >= 3) {
      shapeRef.current = L.polygon(latlngs, {
        color: "#7DB457",
        weight: 2.5,
        fillColor: "#7DB457",
        fillOpacity: 0.28,
      }).addTo(layer);
    } else if (points.length === 2) {
      shapeRef.current = L.polyline(latlngs, { color: "#7DB457", weight: 2.5, dashArray: "6 6" }).addTo(layer);
    }

    points.forEach((p, i) => {
      const marker = L.marker([p.lat, p.lng], { icon: vertexIcon, draggable: true, autoPan: true }).addTo(layer);
      marker.on("drag", () => {
        // live-update the shape geometry without a full re-render
        const lls = markersRef.current.map((m) => m.getLatLng());
        if (shapeRef.current) shapeRef.current.setLatLngs(lls);
      });
      marker.on("dragend", () => {
        const lls = markersRef.current.map((m) => m.getLatLng());
        setPoints(lls.map((ll) => ({ lat: ll.lat, lng: ll.lng })));
      });
      markersRef.current[i] = marker;
    });
  }, [points]);

  const areaM2 = useMemo(() => geodesicAreaM2(points), [points]);
  const areaSqft = areaM2 * SQFT_PER_M2;
  const acres = areaSqft / SQFT_PER_ACRE;

  const undo = () => setPoints((p) => p.slice(0, -1));
  const clear = () => setPoints([]);

  // Debounced autocomplete
  useEffect(() => {
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }
    const q = query.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setShowSug(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/tools/geocode-suggest", { params: { q } });
        setSuggestions(data.suggestions || []);
        setShowSug((data.suggestions || []).length > 0);
        setActiveIdx(-1);
      } catch {
        setSuggestions([]);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSug(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const goTo = (lat, lon) => mapRef.current?.setView([lat, lon], 19, { animate: true });

  const pickSuggestion = (s) => {
    skipFetchRef.current = true;
    setQuery(s.label);
    setShowSug(false);
    setSuggestions([]);
    setGeoError("");
    goTo(s.lat, s.lon);
  };

  const onKeyDown = (e) => {
    if (!showSug || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Escape") {
      setShowSug(false);
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[activeIdx]);
    }
  };

  const search = async (e) => {
    e?.preventDefault();
    if (activeIdx >= 0 && suggestions[activeIdx]) {
      pickSuggestion(suggestions[activeIdx]);
      return;
    }
    const q = query.trim();
    if (!q) return;
    setShowSug(false);
    setSearching(true);
    setGeoError("");
    try {
      const { data } = await api.get("/tools/geocode", { params: { q } });
      goTo(data.lat, data.lon);
    } catch (err) {
      setGeoError(err?.response?.data?.detail || "Search failed. Try adding city, state and ZIP.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div data-testid="lawn-measure-tool" className="grid gap-6 lg:grid-cols-[1fr_320px]">
      {/* Map + search */}
      <div className="rounded-3xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/60 p-4 sm:p-5">
        <form onSubmit={search} className="mb-4 flex gap-2" ref={searchBoxRef}>
          <div className="relative flex-1">
            <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--ld-muted)]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              onFocus={() => suggestions.length > 0 && setShowSug(true)}
              autoComplete="off"
              placeholder="Search address, city, state, ZIP…"
              data-testid="lawn-search-input"
              className="w-full rounded-xl border border-[var(--ld-line)] bg-[var(--ld-bg)] py-2.5 pl-9 pr-3 text-sm font-bold text-[var(--ld-text)] outline-none placeholder:text-[var(--ld-muted)] focus:border-[var(--ld-lime)]"
            />
            {showSug && suggestions.length > 0 && (
              <ul
                data-testid="lawn-suggestions"
                className="absolute left-0 right-0 top-full z-[1000] mt-2 max-h-72 overflow-auto rounded-xl border border-[var(--ld-line)] bg-[var(--ld-surface)] py-1 shadow-2xl shadow-black/50"
              >
                {suggestions.map((s, i) => (
                  <li key={`${s.lat}-${s.lon}-${i}`}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIdx(i)}
                      onClick={() => pickSuggestion(s)}
                      data-testid={`lawn-suggestion-${i}`}
                      className={`flex w-full items-start gap-2 px-3 py-2.5 text-left text-xs font-semibold leading-snug transition-colors ${
                        activeIdx === i ? "bg-[var(--ld-lime)]/15 text-[var(--ld-text)]" : "text-[var(--ld-muted)] hover:text-[var(--ld-text)]"
                      }`}
                    >
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--ld-lime)]" />
                      <span>{s.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="submit"
            disabled={searching}
            data-testid="lawn-search-btn"
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--ld-lime)] px-4 py-2.5 text-sm font-black text-[var(--ld-bg)] transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Find
          </button>
        </form>
        {geoError && (
          <p className="mb-3 text-xs font-bold text-red-400" data-testid="lawn-geo-error">
            {geoError}
          </p>
        )}

        <div
          ref={containerRef}
          data-testid="lawn-map"
          className="h-[380px] w-full overflow-hidden rounded-2xl border border-[var(--ld-line)] sm:h-[440px]"
          style={{ background: "#0b0f0a" }}
        />

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs font-semibold text-[var(--ld-muted)]">
            Click to drop points around your lawn — then drag any point to fine-tune. Add at least 3 to close the shape.
          </p>
          <div className="flex gap-2">
            <button
              onClick={undo}
              disabled={points.length === 0}
              data-testid="lawn-undo-btn"
              className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--ld-line)] px-3 py-2 text-xs font-black text-[var(--ld-text)] transition-colors hover:border-[var(--ld-lime)]/50 disabled:opacity-40"
            >
              <Undo2 className="h-3.5 w-3.5" /> Undo
            </button>
            <button
              onClick={clear}
              disabled={points.length === 0}
              data-testid="lawn-clear-btn"
              className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--ld-line)] px-3 py-2 text-xs font-black text-[var(--ld-text)] transition-colors hover:border-red-400/50 disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear
            </button>
          </div>
        </div>
      </div>

      {/* Result */}
      <div
        data-testid="lawn-result"
        className="flex flex-col rounded-3xl border border-[var(--ld-lime)]/25 bg-gradient-to-b from-[var(--ld-surface)] to-[var(--ld-bg)] p-6 sm:p-7"
      >
        <div className="mb-5 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.22em] text-[var(--ld-lime)]">
            <Ruler className="h-3.5 w-3.5" /> Lawn area
          </span>
          <button
            onClick={() => setMetric((m) => !m)}
            data-testid="lawn-unit-toggle"
            className="rounded-full border border-[var(--ld-line)] px-3 py-1.5 text-[11px] font-black text-[var(--ld-text)] transition-colors hover:border-[var(--ld-lime)]/50"
          >
            {metric ? "m²" : "sq ft"}
          </button>
        </div>

        <div className="flex items-end gap-2">
          <span className="ld-display break-all text-4xl leading-none text-[var(--ld-lime-bright)] sm:text-5xl" data-testid="lawn-area-value">
            {metric ? fmt(areaM2, 1) : fmt(areaSqft)}
          </span>
          <span className="mb-1 shrink-0 text-lg font-black text-[var(--ld-text)]">{metric ? "m²" : "sq ft"}</span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 border-t border-[var(--ld-line)] pt-4">
          <div className="min-w-0">
            <div className="ld-display break-all text-xl text-[var(--ld-text)]" data-testid="lawn-area-alt">
              {metric ? fmt(areaSqft) : fmt(areaM2, 1)}
            </div>
            <div className="text-[10px] font-black uppercase tracking-wider text-[var(--ld-muted)]">
              {metric ? "sq ft" : "m²"}
            </div>
          </div>
          <div className="min-w-0">
            <div className="ld-display break-all text-xl text-[var(--ld-text)]" data-testid="lawn-area-acres">
              {fmt(acres, 3)}
            </div>
            <div className="text-[10px] font-black uppercase tracking-wider text-[var(--ld-muted)]">acres</div>
          </div>
        </div>

        <div className="mt-auto pt-6">
          <p className="text-xs font-semibold leading-relaxed text-[var(--ld-muted)]">
            {points.length < 3
              ? `Drop ${3 - points.length} more point${3 - points.length === 1 ? "" : "s"} to measure.`
              : `${points.length} points placed. Drag any point to adjust, Undo to step back, or Clear to start over.`}
          </p>
        </div>
      </div>
    </div>
  );
}
